// Thin fetch wrappers around the /api/collage/* endpoints. All return
// JSON or throw with the server's error message.

async function jsonOk(res) {
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body?.error) detail = body.error;
    } catch {}
    throw new Error(detail);
  }
  return res.json();
}

function excludeParam(ids) {
  if (!ids || ids.length === 0) return "";
  return `&exclude=${ids.join(",")}`;
}

export async function fetchPalette({ n = 8, exclude = [] } = {}) {
  const r = await fetch(`/api/collage/palette?n=${n}${excludeParam(exclude)}`);
  const d = await jsonOk(r);
  return d.tiles;
}

export async function fetchSiblings(segmentId, { exclude = [], n = 32 } = {}) {
  const r = await fetch(
    `/api/collage/segment/${segmentId}/siblings?n=${n}${excludeParam(exclude)}`,
  );
  const d = await jsonOk(r);
  return d.tiles;
}

export async function fetchNeighbors(segmentId) {
  const r = await fetch(`/api/collage/segment/${segmentId}/neighbors`);
  const d = await jsonOk(r);
  return d.neighbors;
}

export async function postRecompose(anchorId, memberIds) {
  const r = await fetch("/api/collage/recompose", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ anchorId, memberIds }),
  });
  const d = await jsonOk(r);
  return d.added;
}

export async function fetchCanvas() {
  const r = await fetch("/api/collage/canvas");
  return jsonOk(r);
}

export async function putCanvas(payload) {
  const r = await fetch("/api/collage/canvas", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload }),
  });
  return jsonOk(r);
}

// ---- Multi-canvas file management -----------------------------------------

export async function fetchCanvasList() {
  const r = await fetch("/api/collage/canvases");
  const d = await jsonOk(r);
  return d.canvases;
}

export async function fetchActiveCanvas() {
  const r = await fetch("/api/collage/canvases/active");
  const d = await jsonOk(r);
  return d.canvas;
}

export async function fetchCanvasById(id) {
  const r = await fetch(`/api/collage/canvases/${id}`);
  const d = await jsonOk(r);
  return d.canvas;
}

export async function createCanvas({ name, payload } = {}) {
  const r = await fetch("/api/collage/canvases", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, payload }),
  });
  const d = await jsonOk(r);
  return d.canvas;
}

export async function putCanvasById(id, payload) {
  const r = await fetch(`/api/collage/canvases/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload }),
  });
  return jsonOk(r);
}

export async function patchCanvasName(id, name) {
  const r = await fetch(`/api/collage/canvases/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  return jsonOk(r);
}

export async function openCanvas(id) {
  const r = await fetch(`/api/collage/canvases/${id}/open`, { method: "POST" });
  return jsonOk(r);
}

export async function deleteCanvasById(id) {
  const r = await fetch(`/api/collage/canvases/${id}`, { method: "DELETE" });
  return jsonOk(r);
}

// ---- Favorites -------------------------------------------------------------

export async function fetchFavoriteIds() {
  const r = await fetch("/api/collage/favorites/ids");
  const d = await jsonOk(r);
  return d.ids;
}

export async function fetchFavorites() {
  const r = await fetch("/api/collage/favorites");
  const d = await jsonOk(r);
  return d.favorites;
}

export async function addFavoriteApi(segmentId) {
  const r = await fetch(`/api/collage/favorites/${segmentId}`, { method: "POST" });
  return jsonOk(r);
}

export async function removeFavoriteApi(segmentId) {
  const r = await fetch(`/api/collage/favorites/${segmentId}`, { method: "DELETE" });
  return jsonOk(r);
}

export async function fetchInfo() {
  const r = await fetch("/api/info");
  return jsonOk(r);
}

// Base URL for the segment cutout media. In production the ~95k cutouts are
// served from a public Cloudflare R2 bucket rather than this server's own
// /segments/ route, so the droplet never has to carry the 7.4GB library. Set
// at build time:
//   VITE_MEDIA_BASE_URL=https://<r2-public-host>/<prefix>
// pointing at the directory that directly contains the *_segNN.png files.
// Left unset (local dev) it falls back to the server's local /segments/ static
// route. The value is a public URL, not a secret — safe to bake into the bundle.
const MEDIA_BASE = (import.meta.env?.VITE_MEDIA_BASE_URL || "").replace(/\/+$/, "");

export function segmentUrl(file) {
  const key = encodeURIComponent(file);
  return MEDIA_BASE ? `${MEDIA_BASE}/${key}` : `/segments/${key}`;
}
